import dayjs, { type Dayjs } from 'dayjs'
import { resolvePastWindowStart } from '@sportninja/common/features/event/resolvePastWindowStart'
import type { TimePeriod } from './types'

export interface EventDateWindowArgs {
  dateRange: [Dayjs, Dayjs] | null
  timePeriod: TimePeriod
  /**
   * The selected schedule's `starts_at`, when one is selected. PAST reaches
   * back to it rather than to the flat 2-year floor — a schedule cannot hold
   * anything earlier than it began.
   */
  scheduleStartsAt?: string | null
}

export interface EventDateWindow {
  startsAfter: string | null
  startsBefore: string | null
}

/**
 * The date window events are filtered to, derived from the same filter state
 * the games query uses.
 *
 * Mirrors `MyScheduleContext`'s own `getDateRange`, which is provider-internal
 * and not exposed on the context value. Kept in sync deliberately rather than
 * widening the context's public surface for one consumer: a custom range wins
 * over the time period, UPCOMING includes today, and PAST reaches back to the
 * selected schedule's start, or 2 years when nothing is selected.
 *
 * Both bounds are widened to whole days so an event later in the day on the
 * final day of a range is not dropped.
 */
export const resolveEventDateWindow = ({
  dateRange,
  timePeriod,
  scheduleStartsAt,
}: EventDateWindowArgs): EventDateWindow => {
  if (dateRange) {
    return {
      startsAfter: dateRange[0].startOf('day').format(),
      startsBefore: dateRange[1].endOf('day').format(),
    }
  }

  switch (timePeriod) {
    case 'TODAY':
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(1, 'day').format(),
      }
    case 'PAST':
      return {
        // Anchored to the selected schedule's own start when one is picked,
        // else floored at 2 years. Previously open-ended, which asked for the
        // viewer's entire history to render one tab.
        startsAfter: resolvePastWindowStart(scheduleStartsAt).format(),
        startsBefore: dayjs().startOf('day').format(),
      }
    case 'UPCOMING':
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(180, 'day').format(),
      }
    default:
      // Same fallback as the provider's `getDateRange`: a today-shaped window.
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(1, 'day').format(),
      }
  }
}
